import { useFormikContext } from 'formik';
import { debounce } from 'lodash';
import isEqual from 'lodash/isEqual';
import { useEffect, useRef } from 'react';

function usePrevious(value: any) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

interface AutoSubmitProps {
  saveOnFirstRender?: boolean;
  editActive?: boolean;
  /** When false, cancels any pending debounce and suppresses all saves. Pass eventReady here. */
  enabled?: boolean;
}

const AutoSubmit = ({ saveOnFirstRender = false, editActive = false, enabled = true }: AutoSubmitProps) => {
  const { dirty, initialValues, isValid, values, submitForm } = useFormikContext();
  const previousValues = usePrevious(values);
  const previousInitialValues = usePrevious(initialValues);
  const prevEditActive = usePrevious(editActive);
  const firstRender = useRef(true);
  const changes = !isEqual(values, previousValues);
  const reinitialized =
    previousInitialValues !== undefined && !isEqual(initialValues, previousInitialValues);

  // Per-instance debounce so multiple AutoSubmit components don't share a timer
  const debouncedSubmitRef = useRef(debounce((fn: () => void) => fn(), 1000));

  useEffect(() => {
    const debouncedSubmit = debouncedSubmitRef.current;
    if (!enabled) {
      debouncedSubmit.cancel();
      return;
    }
    if (reinitialized) {
      debouncedSubmit.cancel();
      firstRender.current = false;
      return;
    }
    if (!isValid) return;
    if (editActive) return;
    // submit when editActive goes from true to false (onBlur)
    if (prevEditActive === true && editActive === false) {
      if (dirty) debouncedSubmit(submitForm);
      return;
    }
    // dont save on first render unless explicitly set
    if (firstRender.current) {
      firstRender.current = false;
      if (saveOnFirstRender) debouncedSubmit(submitForm);
      return;
    }
    if (changes && dirty) {
      debouncedSubmit(submitForm);
      firstRender.current = false;
    }
  }, [enabled, dirty, initialValues, isValid, values, submitForm, saveOnFirstRender, changes, editActive, prevEditActive, reinitialized]);

  // Cancel the debounce on useEffect cleanup.
  useEffect(() => {
    const debouncedSubmit = debouncedSubmitRef.current;
    return () => debouncedSubmit.cancel();
  }, []);

  return null;
};

export default AutoSubmit;
